import "./style.scss";

import { ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import Chip from "@amzn/stencil-react-components/dist/submodules/chip/Chip";
import { Col, Flex, GridContainer, GridItem, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import IconInformation from "@amzn/stencil-react-icons/icons/icon-information";
import { IconSize } from "@amzn/stencil-react-icons/icons/interfaces";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { TrafficFrom } from "common/helpers/traffic-from";
import { SearchResultProps } from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import NoLogoImage from "img/no_logo_image.png";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { FoundationalChip } from "../FoundationalChip";

const STRING_PATH = "searchResults.searchResult";
const VALUE_PICK_TAG = "Foundational"; // foundational tag from apprat
const chipColorMap: Record<string, ChipBackgroundColor> = {
  os: ChipBackgroundColor.Default,
  availability: ChipBackgroundColor.Yellow,
  approval: ChipBackgroundColor.Pink,
  recommendations: ChipBackgroundColor.Blue,
};

export const SearchResultV2: FC<SearchResultProps> = (props: SearchResultProps) => {
  const history = useHistory();
  let detailsUrl = "#";
  const tags: string[] = props.tags ?? [];
  const recommendations = props.recommendations ?? "";
  const approval: string | undefined = props.approval ?? "";
  const IconInformationWithTooltip = withTooltip()(IconInformation);

  // Since some catalog items will not have a strain value, parsing the taxonomy
  // could throw an error, handling this with a try/catch in the meantime
  try {
    detailsUrl = `items/${ItemTaxonomyId.Parse(props.taxonomyId!).getUrlSuffix()}?from=${TrafficFrom.Search}`;
  } catch (error) {
    // TODO: report this error to cloudwatch. (To be resolved by ITMP-344)
  }

  const trackUxmSearchResultClick = () => {
    const trackerId = config.analytics.uxmAppId;
    const searchResultPayload = {
      isValuePick: tags.includes(VALUE_PICK_TAG),
      name: props.name,
      taxonomyId: props.taxonomyId,
    };

    trackCustomEvent(searchResultPayload, "search-experience-result", trackerId);
  };

  const onSearchResultClick = (event: any) => {
    trackUxmSearchResultClick();

    if (detailsUrl !== "#") {
      if (event.ctrlKey) return true; // allows a new tab if ctrl + click

      history.push(detailsUrl);
      event.preventDefault();
    }
  };

  const getValuePickChip = () => {
    return <FoundationalChip />;
  };

  const getProductLabelChips = (labels: string[], type: string, key: string): JSX.Element[] => {
    const labelChips: JSX.Element[] = [];
    const color = chipColorMap[type] ?? ChipBackgroundColor.Default;

    labels.forEach((label) => {
      labelChips.push(
        <Chip
          key={`${label}-${key}`}
          backgroundColor={color}
          size={ChipSize.ExtraSmall}
          dataTestId={`catalog-product-label-${type}`}
        >
          {label}
          {label === getString(`${STRING_PATH}.usedByTeam`) && (
            <IconInformationWithTooltip
              title="Show tooltip"
              tooltipText={getString(`${STRING_PATH}.usedByTeamTooltip`)}
              size={IconSize.ExtraSmall}
            />
          )}
          {label === getString(`${STRING_PATH}.usedByOrg`) && (
            <IconInformationWithTooltip
              title="Show tooltip"
              tooltipText={getString(`${STRING_PATH}.usedByOrgTooltip`)}
              size={IconSize.ExtraSmall}
            />
          )}
        </Chip>
      );
    });
    return labelChips;
  };

  const renderProductInfo = () => {
    return (
      <View flex={1} padding="S300" justifyContent="flex-start" dataTestId="catalog-product-info">
        <Row gridGap={"S300"}>
          {/* Shadow */}
          <View
            display="flex"
            height={"78px"}
            padding={"S100"}
            alignItems="flex-start"
            className="img-square-wrapper"
            style={{ borderRadius: "16px" }}
          >
            {/* Logo image */}
            <img
              alt={props.name}
              src={props.logoUrl.trim() !== "" ? props.logoUrl : NoLogoImage}
              data-testid="catalog-item-url"
              data-test-id="catalog-item-url"
              data-details-logo="details-logo"
              style={{
                borderRadius: "16px",
                width: "70px",
                height: "70px",
                objectFit: "contain",
              }}
            />
          </View>

          {/* Title and description */}
          <Col gridGap={"S200"}>
            <Row alignItems="center" gridGap={"S200"} dataTestId="catalog-product-title">
              <Text fontSize="T200" fontWeight="bold" color="neutral90" lineHeight="24px" hyphens="initial">
                {props.name}
              </Text>
              {/* // Check against lowercase both sides  */}
              {tags.includes(VALUE_PICK_TAG) && getValuePickChip()}
            </Row>
            <Text
              fontSize="T100"
              fontWeight="regular"
              color="neutral90"
              lineHeight="24px"
              className="product-short-description"
              dataTestId="catalog-product-short-description"
            >
              {props.shortDescription}
            </Text>
          </Col>
        </Row>
      </View>
    );
  };

  const renderPrice = () => {
    if (props.price && parseFloat(props.price) > 0) {
      return (
        <View
          flex={1}
          padding="S300"
          color="neutral90"
          display="flex"
          justifyContent="flex-end"
          dataTestId="catalog-product-price"
        >
          <Text>{`$${parseFloat(props.price).toFixed(2)}`}</Text>
        </View>
      );
    } else {
      const NO_COST_LABEL = tags.includes(VALUE_PICK_TAG)
        ? getString(`${STRING_PATH}.noAdditionalCost`)
        : getString(`${STRING_PATH}.noCost`);

      return (
        <View
          flex={1}
          padding="S300"
          color="neutral90"
          display="flex"
          justifyContent="flex-end"
          dataTestId="catalog-product-no-price"
        >
          <Row gridGap={"S100"} alignItems="center">
            <IconInformationWithTooltip
              title="Show tooltip"
              tooltipText={getString(`${STRING_PATH}.noCostTooltip`)}
              size={IconSize.ExtraSmall}
            />
            <Text>{NO_COST_LABEL}</Text>
          </Row>
        </View>
      );
    }
  };

  const renderProductLabels = () => {
    let labelChips: JSX.Element[] = [];
    if (props.os && props.os.length > 0) {
      labelChips = labelChips.concat(getProductLabelChips(props.os, "os", props.name));
    }
    if (approval && approval.length > 0) {
      labelChips = labelChips.concat(getProductLabelChips([approval], "approval", props.name));
    }
    if (recommendations && recommendations.length > 0) {
      labelChips = labelChips.concat(getProductLabelChips([recommendations], "recommendations", props.name));
    }
    const getEtaRange = (eta: number) => {
      return `${eta}-${eta + 1}`;
    };

    const etaString =
      props.eta && props.eta > 0 && props.hasStock != true
        ? getString(`${STRING_PATH}.eta`, { eta: getEtaRange(props.eta) })
        : getString(`${STRING_PATH}.noEta`);
    labelChips = labelChips.concat(getProductLabelChips([etaString], "availability", props.name));

    return (
      <View flex={1} padding="S300" dataTestId="catalog-product-labels-section">
        <Flex flexDirection={"center"} flexWrap={"wrap"} gridGap={"S200"} justifyContent="flex-end">
          {labelChips}
        </Flex>
      </View>
    );
  };

  const memoizedProductLabels = useMemo(
    () => renderProductLabels(),
    [props.os, props.approval, props.recommendations, props.eta]
  );

  return (
    <>
      <View
        margin={["20px", "0", "20px", "0"]}
        onClick={onSearchResultClick}
        data-cy="catalog-product-result"
        dataTestId="catalog-product-result"
      >
        <a href={detailsUrl} style={{ textDecoration: "none" }}>
          <GridContainer>
            <GridItem lg={6} md={4} sm={2}>
              {renderProductInfo()}
            </GridItem>
            <GridItem lg={2} md={2} sm={2}>
              {renderPrice()}
            </GridItem>
            <GridItem lg={4} md={2} sm={2}>
              {memoizedProductLabels}
            </GridItem>
          </GridContainer>
        </a>
      </View>
      <Hr />
    </>
  );
};
